/**
 * Check-in screen — ONE active round at a time (auto-selected = first
 * not-yet-done round for today). Rounds come from the tenant's
 * REQUIRE_LUNCH_SCAN setting: 2 rounds (morning_in / evening_out) by
 * default, or 4 rounds (adds lunch_out / lunch_in) when enabled. Big live
 * clock + clear round label + a single camera button so users don't
 * accidentally check in to the wrong slot.
 *
 * Past + future rounds are summarized as a progress strip at the bottom;
 * tapping a completed step expands its photo.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CheckCircle2, AlertTriangle, MapPin, Sparkles, Sunrise, Utensils, Sun, Sunset } from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/th';
import { PageLayout } from '../components/layout/PageLayout';
import { Loading } from '../components/layout/Loading';
import { Modal } from '../components/ui/Modal';
import { useCheckinStatus, useSubmitCheckin, useGeolocation } from '../hooks';
import { useAuthStore } from '../lib/store';
import { resolveFileUrl } from '../lib/api-client';
import { watermarkImage } from '../lib/watermark';
import type { CheckinKind } from '@payroll/shared';

dayjs.locale('th');

// Default order; the actual rounds shown come from `status.kinds` (the tenant's
// REQUIRE_LUNCH_SCAN setting decides 2 vs 4 rounds). This is only a fallback.
const ORDER: CheckinKind[] = ['morning_in', 'lunch_out', 'lunch_in', 'evening_out'];

const KIND_META: Record<CheckinKind, { title: string; sub: string; icon: typeof Camera }> = {
  morning_in: { title: 'เข้างาน', sub: 'ตอนเช้า', icon: Sunrise },
  lunch_out: { title: 'พักเที่ยง', sub: 'ออกพัก', icon: Utensils },
  lunch_in: { title: 'กลับจากพัก', sub: 'หลังเที่ยง', icon: Sun },
  evening_out: { title: 'เลิกงาน', sub: 'ตอนเย็น', icon: Sunset },
};

function KindIcon({ kind, className }: { kind: CheckinKind; className?: string }) {
  const Icon = KIND_META[kind].icon;
  return <Icon className={className} />;
}

/**
 * Clock that updates once a minute — *not* every second.
 *
 * The 1-second tick used to live here and was the prime suspect for the
 * "กดถ่ายแล้วไม่มีอะไรเกิด" bug on iOS LIFF: setInterval-driven re-renders
 * race with the dismissal of `<input type="file" capture>` and the onChange
 * event silently disappears. HH:mm precision is fine for an attendance UI —
 * users don't need a stopwatch.
 */
function useTickEveryMinute() {
  const [, force] = useState(0);
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);
}

interface CheckinSuccess {
  kind: CheckinKind;
  date: string;
  capturedAt: string; // ISO
  shiftName: string | null;
  shiftStart: string | null;
  lateMinutes: number;
  geofencePassed: boolean;
  approvalStatus: string;
  warning: string | null;
  locationMode: 'office' | 'wfh' | undefined;
}

export function Checkin() {
  useTickEveryMinute();
  const navigate = useNavigate();
  const { data: status, isLoading, refetch } = useCheckinStatus();
  const submit = useSubmitCheckin();
  const getLocation = useGeolocation();
  const profile = useAuthStore((s) => s.profile);
  const fileRef = useRef<HTMLInputElement>(null);
  const [expandedKind, setExpandedKind] = useState<CheckinKind | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [lateMsg, setLateMsg] = useState<string | null>(null);
  const [warnMsg, setWarnMsg] = useState<string | null>(null);
  const [success, setSuccess] = useState<CheckinSuccess | null>(null);
  // Hybrid ต้องเลือกก่อนเช็คอินแต่ละรอบว่าวันนี้ทำงานที่ไหน — เคลียร์ค่าทุกครั้งที่ขึ้นรอบใหม่
  const [locationChoice, setLocationChoice] = useState<'office' | 'wfh' | null>(null);

  // Rounds for today come from the tenant setting (2 or 4). Fall back to the
  // full 4 only if the API somehow omits `kinds`.
  const rounds = useMemo<CheckinKind[]>(
    () => (status?.kinds?.length ? [...status.kinds] : ORDER),
    [status],
  );
  const roundCount = rounds.length;

  const activeKind = useMemo<CheckinKind | null>(() => {
    if (!status) return null;
    return rounds.find((k) => !status.events?.[k]) ?? null;
  }, [status, rounds]);

  const workMode = status?.work_mode ?? 'office';
  useEffect(() => { setLocationChoice(null); }, [activeKind]);

  if (isLoading || !status) {
    return (
      <PageLayout title="เช็คอินด้วยรูป">
        <Loading />
      </PageLayout>
    );
  }

  // พนักงานที่ยกเว้นการสแกน (จ่ายเต็มไม่นับเวลา) → ไม่ต้องเช็คอิน
  if (status.attendance_exempt) {
    return (
      <PageLayout title="เช็คอินด้วยรูป">
        <div className="card p-6 text-center">
          <CheckCircle2 className="w-14 h-14 mx-auto text-green-600 mb-3" />
          <h2 className="text-lg font-bold mb-1">ไม่ต้องสแกนเข้างาน</h2>
          <p className="text-sm text-gray-600">
            บัญชีของคุณถูกตั้งเป็น “ไม่นับเวลาเข้า–ออกงาน” — รับเงินเดือนเต็มโดยไม่ต้องเช็คอิน
          </p>
          <p className="text-xs text-gray-400 mt-2">หากคิดว่าผิดพลาด ติดต่อ HR/เจ้าของ</p>
        </div>
      </PageLayout>
    );
  }

  const now = new Date();
  const allDone = activeKind === null;

  async function handleCapture(kind: CheckinKind, raw: File) {
    setErrMsg(null);
    try {
      const loc = await getLocation();

      const nowLocal = new Date();
      const stamp = dayjs(nowLocal).format('D MMM YYYY · HH:mm');

      const watermarked = await watermarkImage({
        source: raw,
        lines: [
          `${KIND_META[kind].title} · ${KIND_META[kind].sub}`,
          `${profile?.display_name ?? ''} (${profile?.emp_code ?? ''})`,
          stamp,
        ],
      });

      setLateMsg(null);
      // ⭐ เดิมตั้งข้อความ "ไม่สามารถระบุพิกัดได้" ไว้ด้านบน แล้วมาโดน setWarnMsg(null)
      // บรรทัดนี้ล้างทิ้งก่อนหน้าจอ render — พนักงาน 86 คนส่งพิกัด (0,0) มาโดยไม่เคย
      // เห็นคำเตือนเลยสักครั้ง. ตอนนี้ย้ายไปตั้งหลังส่งสำเร็จแทน (ดูด้านล่าง)
      setWarnMsg(null);

      const locationMode: 'office' | 'wfh' | undefined =
        workMode === 'wfh' ? 'wfh' : workMode === 'hybrid' ? (locationChoice ?? undefined) : undefined;

      const res = await submit.mutateAsync({
        file: watermarked,
        filename: `${profile?.emp_code ?? 'user'}_${kind}_${nowLocal.getTime()}.jpg`,
        latitude: loc.latitude,
        longitude: loc.longitude,
        accuracy: loc.accuracy,
        kind,
        location_mode: locationMode,
      });
      if (kind === 'morning_in' && res.late_minutes > 0) {
        setLateMsg(`เข้างานสาย ${res.late_minutes} นาที${res.shift_start ? ` (กะเริ่ม ${res.shift_start})` : ''}`);
      }
      if (res.warning) setWarnMsg(res.warning);
      setSuccess({
        kind,
        date: res.date,
        capturedAt: new Date().toISOString(),
        shiftName: res.shift_name ?? null,
        shiftStart: res.shift_start ?? null,
        lateMinutes: res.late_minutes ?? 0,
        geofencePassed: res.geofence_passed ?? true,
        approvalStatus: res.approval_status ?? 'auto_ok',
        warning: res.warning ?? null,
        locationMode,
      });
      await refetch();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown error';
      setErrMsg(msg);
    }
  }

  return (
    <PageLayout title="เช็คอินด้วยรูป">
      {/* === Live clock + active round headline === */}
      <div className="card p-5 mb-4 bg-brand-gradient text-white">
        <div className="text-xs opacity-90">{dayjs(now).format('dddd D MMMM YYYY')}</div>
        <div className="mt-1 font-mono text-5xl font-bold tabular-nums leading-none">
          {dayjs(now).format('HH:mm')}
        </div>

        {allDone ? (
          <div className="mt-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6" />
            <div>
              <div className="font-bold text-lg">เช็คอินครบ {roundCount} รอบแล้ว ✓</div>
              <div className="text-xs opacity-90">ขอบคุณค่ะ — พบกันใหม่พรุ่งนี้</div>
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <div className="text-xs opacity-90">
              รอบที่ {rounds.indexOf(activeKind) + 1} จาก {roundCount}
            </div>
            <div className="text-2xl font-bold mt-0.5 flex items-center gap-2">
              <KindIcon kind={activeKind} className="w-6 h-6" />
              <span>{KIND_META[activeKind].title}</span>
            </div>
            <div className="text-sm opacity-90 mt-0.5">
              {KIND_META[activeKind].sub} · {status.shift ? 'ตามกะ' : 'เวลามาตรฐาน'} {status.windows?.[activeKind] ?? '—'}
            </div>
          </div>
        )}
      </div>

      {/* === Today's shift === */}
      {status.shift && (
        <div className="card p-3 mb-4 flex items-center gap-2 text-sm">
          <span className="text-gray-500">กะวันนี้</span>
          <span className="font-medium">{status.shift.name}</span>
          <span className="text-gray-400">· {status.shift.start_time}–{status.shift.end_time}</span>
        </div>
      )}

      {lateMsg && (
        <div className="card p-3 mb-4 bg-gold-wash border-gold/40 text-gold-deep text-sm">
          ⏰ {lateMsg}
        </div>
      )}

      {warnMsg && (
        <div className="card p-3 mb-4 bg-gold-wash border-gold/40 text-gold-deep text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{warnMsg}</span>
        </div>
      )}

      {/* === Hybrid: เลือกก่อนว่าวันนี้ทำงานที่ไหน === */}
      {!allDone && workMode === 'hybrid' && !locationChoice && (
        <div className="card p-4 mb-4">
          <div className="font-semibold text-sm mb-2">วันนี้ทำงานที่:</div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setLocationChoice('office')} className="btn-secondary justify-center">
              ออฟฟิศ
            </button>
            <button type="button" onClick={() => setLocationChoice('wfh')} className="btn-secondary justify-center">
              ที่บ้าน
            </button>
          </div>
        </div>
      )}

      {/* === Big action button === */}
      {!allDone && (workMode !== 'hybrid' || locationChoice) && (
        <button
          className="btn-cta w-full text-lg py-4 mb-4 disabled:opacity-50"
          onClick={() => fileRef.current?.click()}
          disabled={submit.isPending}
        >
          <Camera className="w-6 h-6" />
          {submit.isPending ? 'กำลังอัปโหลด…' : `ถ่ายรูปเพื่อ${KIND_META[activeKind].title}`}
        </button>
      )}

      {errMsg && (
        <div className="card p-3 mb-4 bg-red-50 border-red-200 text-red-700 text-sm">
          เกิดข้อผิดพลาด: {errMsg}
        </div>
      )}

      {/* === per-round status strip (2 or 4 rounds) === */}
      <div className="card p-4">
        <div className="font-semibold mb-3">สถานะวันนี้</div>
        <div className="space-y-2">
          {rounds.map((kind, idx) => {
            const ev = status.events?.[kind];
            const done = !!ev;
            const isActive = kind === activeKind;
            const isExpanded = expandedKind === kind;
            return (
              <div key={kind}>
                <button
                  type="button"
                  onClick={() => done && setExpandedKind(isExpanded ? null : kind)}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg transition ${
                    isActive
                      ? 'bg-brand-50 border border-brand-500'
                      : done
                      ? 'hover:bg-gray-50 cursor-pointer'
                      : 'opacity-60'
                  }`}
                >
                  <span className="w-8 flex justify-center"><KindIcon kind={kind} className={`w-6 h-6 ${isActive ? 'text-brand-600' : 'text-gray-400'}`} /></span>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-sm flex items-center gap-1">
                      {idx + 1}. {KIND_META[kind].title}
                      {isActive && (
                        <span className="text-xs text-brand-700 ml-1">(รอบปัจจุบัน)</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      เวลามาตรฐาน {status.windows?.[kind] ?? '—'}
                      {done && (() => {
                        const at = (ev as { captured_at?: string })?.captured_at;
                        return at ? ` · บันทึก ${new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Bangkok' }).format(new Date(at))}` : '';
                      })()}
                    </div>
                  </div>
                  {done ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  ) : isActive ? (
                    <span className="w-2.5 h-2.5 rounded-full bg-brand-500 shrink-0 animate-pulse" />
                  ) : (
                    <span className="w-2.5 h-2.5 rounded-full bg-gray-300 shrink-0" />
                  )}
                </button>

                {/* Expanded preview when tapping a done step */}
                {done && isExpanded && (
                  <div className="px-2 pt-1 pb-2">
                    {(ev as { selfie_url?: string | null })?.selfie_url && (
                      <img
                        src={resolveFileUrl((ev as { selfie_url?: string | null }).selfie_url) ?? ''}
                        alt={KIND_META[kind].title}
                        className="w-full rounded-lg border border-gray-200"
                        loading="lazy"
                      />
                    )}
                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
                      {(ev as { geofence_passed?: boolean | null })?.geofence_passed === true && (
                        <span className="flex items-center gap-0.5 text-green-700">
                          <MapPin className="w-3 h-3" /> ในพื้นที่
                        </span>
                      )}
                      {(ev as { geofence_passed?: boolean | null })?.geofence_passed === false && (
                        <span className="flex items-center gap-0.5 text-gold-deep">
                          <AlertTriangle className="w-3 h-3" /> นอกพื้นที่
                        </span>
                      )}
                      {(ev as { approval_status?: string })?.approval_status === 'pending_review' && (
                        <span className="text-gold-deep">รอตรวจสอบ</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) {
            setErrMsg('ไม่ได้รับไฟล์จากกล้อง — ลองกดถ่ายใหม่อีกครั้ง');
          } else if (!activeKind) {
            setErrMsg('สแกนครบทุกรอบแล้ว — ไม่มีรอบที่ต้องสแกน');
          } else {
            handleCapture(activeKind, file);
          }
          e.target.value = '';
        }}
      />

      {/* Success bottom sheet — ค้างจนกว่าจะกดปุ่ม (ไม่ปิดเอง) */}
      <Modal open={!!success} onClose={() => setSuccess(null)}>
        <div className="text-center pt-2">
          <CheckCircle2 className="w-14 h-14 mx-auto text-green-600 mb-2" />
          <h2 className="text-lg font-bold mb-1">ลงเวลาสำเร็จ</h2>
          <p className="font-mono text-2xl font-bold text-navy-800 my-1">
            {success
              ? new Intl.DateTimeFormat('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                  timeZone: 'Asia/Bangkok',
                }).format(new Date(success.capturedAt))
              : ''}
          </p>
          <p className="text-sm text-gray-600">
            {success ? KIND_META[success.kind].title : ''}
            {success?.shiftName ? ` · ${success.shiftName}` : ''}
          </p>
          {success && success.lateMinutes > 0 && (
            <p className="text-sm text-gold-deep mt-2">⏰ สาย {success.lateMinutes} นาที</p>
          )}
          {success && !success.geofencePassed && success.locationMode !== 'wfh' && (
            <p className="text-sm text-gold-deep mt-1 flex items-center justify-center gap-1"><MapPin className="w-4 h-4" /> อยู่นอกพื้นที่ — รอ HR ตรวจสอบ</p>
          )}
          {success?.warning && (
            <p className="text-sm text-gold-deep mt-1 flex items-center justify-center gap-1"><AlertTriangle className="w-4 h-4" /> {success.warning}</p>
          )}
          <p className="text-xs text-gray-400 mt-3">บันทึกแล้ว · จะส่งใบเสร็จไป LINE</p>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setSuccess(null)} className="btn-secondary flex-1">
              เสร็จ
            </button>
            <button
              onClick={() => {
                setSuccess(null);
                navigate('/me/attendance');
              }}
              className="btn-primary flex-1"
            >
              📋 ดูประวัติของฉัน
            </button>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}
